/**
 * LabelAIAssistModal — one-click "fill this whole label for me".
 *
 * Operator types the product name (+ optional description), hits Generate,
 * and the modal runs TWO AI calls in parallel:
 *   1. Claude Sonnet drafts the copy fields (name, ingredients, allergens,
 *      rehydration, storage, notes, warnings).
 *   2. Gemini + googleSearch pulls the freeze-dried Nutrition Facts panel
 *      with cited USDA / FDA sources.
 * Both results are shown for review, then applied together via onApply.
 */

import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Sparkles, Loader2, X, Check, ExternalLink } from 'lucide-react';
import AIProgressIndicator from './AIProgressIndicator';

export default function LabelAIAssistModal({ open, onClose, onApply, defaultProduct = '' }) {
  const { API } = useContext(AuthContext);
  const [productName, setProductName] = useState(defaultProduct);
  const [description, setDescription] = useState('');
  const [includeNutrition, setIncludeNutrition] = useState(true);
  const [loading, setLoading]     = useState(false);
  const [copyLoading, setCopyLoading]   = useState(false);
  const [nutriLoading, setNutriLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [nutrition,   setNutrition]   = useState(null);
  const [sources,     setSources]     = useState([]);

  if (!open) return null;

  const generate = async () => {
    const q = (productName || '').trim() || (description || '').trim();
    if (!q) { toast.error('Type a product name or description first.'); return; }
    setLoading(true);
    setCopyLoading(true);
    setNutriLoading(includeNutrition);
    setSuggestions(null); setNutrition(null); setSources([]);

    // Fire the two AI calls INDEPENDENTLY so the fast one (Claude, ~4s)
    // can render its results while the slow one (Gemini + googleSearch,
    // ~25s) is still working. Users saw the shared "loading" state as
    // "the app froze" — now they see label copy appear immediately.
    const copyReq = axios
      .post(`${API}/label-presets/ai-assist`, {
        product_name: productName.trim(),
        description:  description.trim(),
        freeze_dried: true,
      })
      .then((r) => setSuggestions(r.data))
      .catch((e) =>
        toast.error(e?.response?.data?.detail || 'Label fields lookup failed'),
      )
      .finally(() => setCopyLoading(false));

    let nutriReq = Promise.resolve();
    if (includeNutrition) {
      nutriReq = axios
        .post(`${API}/label-presets/nutrition-lookup`, {
          query:        productName.trim() || description.trim(),
          freeze_dried: true,
        })
        .then((r) => {
          setNutrition(r.data?.data || null);
          setSources(r.data?.sources || []);
        })
        .catch(() =>
          toast.warning('Nutrition lookup failed — label copy still populated.'),
        )
        .finally(() => setNutriLoading(false));
    }

    // Overall "loading" only clears when BOTH have settled — the Apply
    // button waits for the full result so users don't apply a half label
    // by mistake. But the sub-flags drive the two independent
    // progress bars.
    await Promise.allSettled([copyReq, nutriReq]);
    setLoading(false);
  };

  const apply = () => {
    if (!suggestions && !nutrition) return;
    onApply({
      ...(suggestions || {}),
      nutritionData:    nutrition,
      nutritionSources: sources,
    });
    toast.success('Label populated from AI suggestions.');
    onClose();
  };

  const previewRows = [
    ['productName',      'Product name'],
    ['ingredients',      'Ingredients'],
    ['allergens',        'Allergens'],
    ['rehydrationRatio', 'Rehydration'],
    ['storage',          'Storage'],
    ['notes',            'Notes'],
    ['warnings',         'Warnings (bottom of label)'],
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 no-print"
      onClick={onClose}
      data-testid="lm-ai-assist-modal"
    >
      <Card
        className="max-w-2xl w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> Master AI Fill
            </CardTitle>
            <div className="text-xs text-muted-foreground mt-1">
              Fills every label field — copy AND nutrition — from just a product name.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
            data-testid="lm-ai-assist-close"
          >
            <X className="w-4 h-4" />
          </button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Product name</Label>
            <Input
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="e.g. Freeze-Dried Strawberry Slices"
              data-testid="lm-ai-product-name"
            />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Anything special: origin, cut size, sweetener, single-serve, etc."
              data-testid="lm-ai-description"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeNutrition}
              onChange={(e) => setIncludeNutrition(e.target.checked)}
              data-testid="lm-ai-include-nutrition"
            />
            Also fetch Nutrition Facts (adds ~15 s)
          </label>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={generate}
              disabled={loading}
              data-testid="lm-ai-generate"
            >
              {loading
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Sparkles className="w-4 h-4 mr-2" />}
              Generate label
            </Button>
          </div>

          <AIProgressIndicator
            active={copyLoading}
            estimatedMs={8000}
            data-testid="lm-ai-progress-copy"
            label="Drafting label copy"
            steps={[
              { at: 0,    label: 'Drafting label copy (Claude Sonnet)…' },
              { at: 3000, label: 'Polishing ingredients, allergens, warnings…' },
              { at: 6000, label: 'Almost done…' },
            ]}
          />
          <AIProgressIndicator
            active={nutriLoading}
            estimatedMs={26000}
            data-testid="lm-ai-progress-nutri"
            label="Fetching Nutrition Facts"
            steps={[
              { at: 0,     label: 'Searching FDA / USDA references (Gemini + search)…' },
              { at: 6000,  label: 'Extracting per-100g macros + moisture…' },
              { at: 14000, label: 'Concentrating values for freeze-dried state…' },
              { at: 22000, label: 'Compiling panel + verifying citations…' },
            ]}
          />

          {(suggestions || nutrition) && (
            <div
              className="space-y-3 border-t border-border pt-4"
              data-testid="lm-ai-suggestions"
            >
              {suggestions && previewRows.map(([key, label]) => {
                const v = suggestions[key] || '';
                if (!v) return null;
                return (
                  <div key={key}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {label}
                    </div>
                    <div className="text-sm text-foreground/90 whitespace-pre-wrap">
                      {v}
                    </div>
                  </div>
                );
              })}
              {nutrition && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Nutrition Facts (freeze-dried, per {nutrition.serving_size || 'serving'})
                  </div>
                  <div className="text-xs text-foreground/90 grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1">
                    {[
                      ['Calories',      nutrition.calories],
                      ['Total Fat',     nutrition.total_fat_g   != null ? `${nutrition.total_fat_g} g` : null],
                      ['Saturated Fat', nutrition.saturated_fat_g != null ? `${nutrition.saturated_fat_g} g` : null],
                      ['Sodium',        nutrition.sodium_mg     != null ? `${nutrition.sodium_mg} mg` : null],
                      ['Carbs',         nutrition.total_carbs_g != null ? `${nutrition.total_carbs_g} g` : null],
                      ['Fiber',         nutrition.dietary_fiber_g != null ? `${nutrition.dietary_fiber_g} g` : null],
                      ['Sugars',        nutrition.total_sugars_g != null ? `${nutrition.total_sugars_g} g` : null],
                      ['Protein',       nutrition.protein_g     != null ? `${nutrition.protein_g} g` : null],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k}><span className="font-semibold">{k}:</span> {v}</div>
                    ))}
                  </div>
                  {sources.length > 0 && (
                    <div className="text-[11px] text-muted-foreground mt-2 space-y-0.5">
                      {sources.slice(0, 3).map((u) => (
                        <a key={u} href={u} target="_blank" rel="noopener noreferrer"
                           className="flex items-center gap-1 hover:underline break-all">
                          <ExternalLink className="w-3 h-3 shrink-0" /> {u}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end pt-2 items-center gap-2">
                {loading && (
                  <span className="text-xs text-muted-foreground" data-testid="lm-ai-partial-note">
                    Still fetching the rest — you can apply what's ready.
                  </span>
                )}
                <Button
                  type="button"
                  onClick={apply}
                  disabled={!suggestions && !nutrition}
                  data-testid="lm-ai-apply"
                >
                  <Check className="w-4 h-4 mr-2" /> Apply to label
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
